import { yupResolver } from '@hookform/resolvers/yup';
import { SkillSelector, StyledForm } from 'common/components/input';
import { Box, Button, Card, Grid, Stack, Typography } from 'common/components/material';
import { useDrawer, useForm, useMutation, useQuery, useSession } from 'common/hooks';
import { useGetProfile } from 'features/profiles';
import { CXEditorField } from 'libs/toast-ui';
import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { VerticalScrollableContainer } from 'common/components/container';
import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import { EllipsisText } from 'common/components/ellipsis-text/ellipsis-text.component';
import { CardContent } from 'common/components/material/Card';
import { DrawerHeader } from 'common/components/material/Drawer';
import { CloseButton } from 'common/components/navigation';
import { UseMutationOptions, UseQueryOptions } from 'common/types/Api.type';
import SkillChip from 'features/org-root/components/SkillChip';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import {
  RecentlyWorkedItem,
  WorkItem,
} from 'features/profiles/components/EdnorsedSkillTrackRecord';
import { Skill } from 'features/skills';
import moment from 'moment';
import {
  AddFeedbackParams,
  DrawerTitle,
  FeedbackForm,
  FeedbackFormSchema,
  FeedbackList,
  getDateLabel,
  matchingEngineApi,
  PastFeedbackDrawer,
} from '../matching-engine.utils';

export const MatchingEnginePastFeedbackDrawer: React.FC<{
  profileId: any;
}> = ({ profileId }) => {
  const { data, isLoading } = useGetAllPastFeedbacks(profileId);

  if (isLoading || !data || !data.pastFeedbacks) return <></>;

  const { pastFeedbacks } = data;
  return (
    <PastFeedbackDrawer
      revieweeId={profileId}
      drawerId={DrawerIdEnum.MATCHING_ENGINE_PAST_FEEDBACK}
      title={'Manager Feedback'}
    >
      <FeedbackList feedbacks={pastFeedbacks} />
    </PastFeedbackDrawer>
  );
};

export const FeedbackEntry: React.FC<{ feedback: any }> = ({ feedback }) => {
  const { palette } = useTheme();
  const { createdDate, reviewer, feedback: feedbackText } = feedback;

  const labels = [`${getDateLabel(createdDate)}`, `from ${reviewer.fullName}`];
  return (
    <Stack spacing={2}>
      <Box>
        <Stack flexDirection="column" justifyContent="space-between" lineHeight="1.5rem">
          <Box lineHeight={1}>
            <Typography variant="body4" color={palette.Text.SubHeadline}>
              {labels.join(' • ')}
            </Typography>
          </Box>
          <EllipsisText variant="body3" color={palette.Text.Headline} lineclamp={1}>
            {feedbackText}
          </EllipsisText>
        </Stack>
      </Box>
    </Stack>
  );
};

export const MatchingEngineAddFeedbackDrawer: React.FC<{
  profileId: any;
  lastFeedbackDate: string;
  handleFeedbackSubmit?: Function;
}> = ({ profileId, lastFeedbackDate, handleFeedbackSubmit }) => {
  const { closeDrawer } = useDrawer();

  const { data: profile, isLoading } = useGetProfile({ id: profileId });

  if (isLoading) return <></>;

  const closeCurrentDrawer = () => {
    closeDrawer(DrawerIdEnum.MATCHING_ENGINE_ADD_FEEDBACK);
  };

  return (
    <>
      <DrawerHeader
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Stack flexDirection="row" alignItems="center" pl={2} pt={2}>
          <CXAvatar src={profile?.photo} alt={profile?.fullName} />
          <DrawerTitle ml={2}>{profile.fullName}</DrawerTitle>
        </Stack>
        <CloseButton clickHandler={closeCurrentDrawer}></CloseButton>
      </DrawerHeader>
      <Box mx={3} mt={2} display="flex" height="100%">
        <AddFeedbackView
          profileId={profileId}
          lastFeedbackDate={lastFeedbackDate}
          onSubmit={handleFeedbackSubmit}
        />
      </Box>
    </>
  );
};

export const TeamFeedbackContainer = () => {
  const { openDrawer } = useDrawer();

  const { profile } = useSession();

  const { data, isLoading, refetch } = useGetTeamFeedbackInfo(profile.id);

  if (isLoading || !data) return <></>;

  const { teamFeedbackInfo } = data;

  const handleFeedbackSubmit = () => {
    refetch();
  };

  const openAddFeedbackDrawer = (profileId: string, lastFeedbackDate: string) => {
    openDrawer(DrawerIdEnum.MATCHING_ENGINE_ADD_FEEDBACK, {
      profileId,
      lastFeedbackDate,
      handleFeedbackSubmit,
    });
  };

  const openManageSkillDashboardDrawer = (profileId: string) => {
    openDrawer(DrawerIdEnum.MANAGE_SKILL_DASHBOARD_DRAWER, {
      profileId,
    });
  };

  const openShowPreviousFeedbackDrawer = (profileId: string, lastFeedbackDate: string) => {
    openDrawer(DrawerIdEnum.MATCHING_ENGINE_PAST_FEEDBACK, {
      profileId,
    });
  };

  const columns: GridColDef<(typeof teamFeedbackInfo)[number]>[] = [
    { field: 'id', headerName: 'ID', width: 150 },
    {
      field: 'fullName',
      headerName: 'Full name',
      sortable: false,
      width: 200,
    },
    {
      field: 'lastFeedbackDate',
      headerName: 'Last Feedback Date',
      width: 200,
      editable: false,
      renderCell: ({ row }) => {
        const date = row.lastFeedbackDate;
        if (!date) return <></>;
        return moment.utc(date).format('DD MMM YYYY');
      },
    },
    {
      field: 'SeePreviousFeedbackButton',
      headerName: 'Past Feedback',
      headerClassName: 'gridActions',
      renderCell: ({ row }) => {
        if (!row.lastFeedbackDate) return <></>;
        return (
          <Button
            onClick={() =>
              openShowPreviousFeedbackDrawer(row.profileId, row.lastFeedbackDate)
            }
          >
            Past Feedback
          </Button>
        );
      },
      width: 200,
    },
    {
      field: 'AddNewFeedbackButton',
      headerName: 'Add Feedback',
      headerClassName: 'gridActions',
      renderCell: ({ row }) => {
        return (
          <Button onClick={() => openAddFeedbackDrawer(row.profileId, row.lastFeedbackDate)}>
            Add Feedback
          </Button>
        );
      },
      width: 200,
    },
    {
      field: 'ManageSkillsDashboard',
      headerName: 'Manage Skills',
      headerClassName: 'gridActions',
      renderCell: ({ row }) => {
        return (
          <Button onClick={() => openManageSkillDashboardDrawer(row.profileId)}>
            Manage Skills
          </Button>
        );
      },
      width: 200,
    },
  ];

  return (
    <>
      <Box pt={2} sx={{ width: '100%' }}>
        <DataGrid
          rows={teamFeedbackInfo}
          columns={columns}
          hideFooter={true}
          disableRowSelectionOnClick
        />
      </Box>
    </>
  );
};

const AddFeedbackView: React.FC<{
  profileId: string;
  lastFeedbackDate: string;
  onSubmit?: Function;
}> = ({ profileId, lastFeedbackDate, onSubmit }) => {
  const transformedLastFeedbackDate = lastFeedbackDate
    ? moment.utc(lastFeedbackDate).format('DD-MMM-YYYY')
    : moment.utc().subtract(365, 'days').format('DD-MMM-YYYY');

  const { closeDrawer } = useDrawer();

  const cutOffDays = moment
    .utc()
    .diff(moment.utc(transformedLastFeedbackDate, 'DD-MMM-YYYY'), 'days');

  const {
    data: { skillsDemonstrated, workItems },
    isLoading,
    isSuccess,
  } = useGetSkillsDemonstratedForFeedback(profileId, cutOffDays);

  const [endorsedSkillState, setEndorsedSkillState] = useState<{
    [skillId: string]: boolean;
  }>({});

  const [visibleDemonstratedSkills, setVisibleDemonstratedSkills] = useState<Skill[]>([]);

  //we have to wait for the response from api and then refetch
  const { mutateAsync: addFeedback } = useAddFeedback();

  useEffect(() => {
    if (!skillsDemonstrated || !skillsDemonstrated.length) return;

    setVisibleDemonstratedSkills(skillsDemonstrated);

    setEndorsedSkillState(
      (skillsDemonstrated || []).reduce((acc, skill) => {
        acc[`${skill?.id}`] = false;
        return acc;
      }, {} as { [skillId: string]: boolean }),
    );
  }, [isSuccess, skillsDemonstrated]);

  const form = useForm<FeedbackForm>({
    defaultValues: {
      feedback: '',
    },
    mode: 'onChange',
    resolver: yupResolver(FeedbackFormSchema()),
  });

  const {
    getValues,
    formState: { isValid },
  } = form;

  if (isLoading) return <></>;

  const submit = async (e: React.MouseEvent) => {
    e.preventDefault();
    const data = getValues();

    const feedback = {
      revieweeId: profileId,
      skillIds: Object.keys(endorsedSkillState).filter(
        (skillId) => !!endorsedSkillState[skillId],
      ),
      feedback: data.feedback,
    };

    await addFeedback(feedback);
    closeDrawer(DrawerIdEnum.MATCHING_ENGINE_ADD_FEEDBACK);
    onSubmit && onSubmit();
  };

  const handleSkillChange = (skill: any) => {
    if (!skill) return;
    setVisibleDemonstratedSkills((prev) => {
      const isVisible = prev.some(({ id }) => id === skill.id);
      if (isVisible) return prev;
      return [skill, ...prev];
    });

    setEndorsedSkillState((prev) => {
      const updatedSkill = { [skill.id]: skill.id in prev ? !prev[skill.id] : true };
      return Object.assign({}, prev, updatedSkill);
    });
  };

  return (
    <FormProvider {...form}>
      <StyledForm noValidate>
        <Grid container columnSpacing={4} height="100%">
          {!!workItems.length && (
            <Grid item md={6} height="100%">
              <Typography sx={{ textAlign: 'start', pb: 2, pl: 4, mt: 2 }} variant="body1">
                Contributions since {transformedLastFeedbackDate}
              </Typography>
              <VerticalScrollableContainer>
                <Stack gap={2}>
                  {workItems.map((item) => (
                    <Card noElevation key={item.id}>
                      <CardContent>
                        <RecentlyWorkedItem {...item} />{' '}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </VerticalScrollableContainer>
            </Grid>
          )}
          <Grid
            sx={{ '& .cx-editor-wrapper>div>div': { height: '30vh !important' } }}
            item
            md={!!workItems.length ? 6 : 12}
          >
            <Stack gap={2} height="100%" justifyContent="flex-start" paddingBottom={2}>
              <Card noElevation>
                <CardContent>
                  <Stack gap={2}>
                    <Stack
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography display="flex" variant="body1">
                        Skills Demonstrated
                      </Typography>
                      <SkillSelector
                        fullWidth
                        sx={{ width: '30vh' }}
                        excludeOptions={visibleDemonstratedSkills.map(({ id }) => id)}
                        name="skills"
                        label="Add skills"
                        placeholder="Type to search"
                        getOptionLabel={(option) => option?.name}
                        getOptionDisabled={(option) => option?.id}
                        multiple={false}
                        onChange={handleSkillChange}
                      />
                    </Stack>
                    <Stack
                      gap={1}
                      flexDirection="row"
                      flexWrap="wrap"
                      sx={{ maxHeight: '22vh', overflowX: 'auto' }}
                    >
                      {visibleDemonstratedSkills.map((skill) => (
                        <SkillChip
                          key={skill.id}
                          skill={skill}
                          icon={
                            endorsedSkillState[skill.id] ? (
                              <CheckCircleIcon fontSize="small" />
                            ) : (
                              <AddIcon fontSize="inherit" />
                            )
                          }
                          isSelected={endorsedSkillState[skill.id]}
                          onClick={handleSkillChange}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
              <Stack gap={2}>
                <CXEditorField name="feedback" placeholder={`Your feedback`} />
                <Button
                  type="submit"
                  btntype="primary"
                  size="large"
                  disabled={!isValid}
                  onClick={submit}
                >
                  Submit
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </StyledForm>
    </FormProvider>
  );
};

////////////  HOOKS ///////////////////////////////

export const useGetAllPastFeedbacks = (profileId: string, options?: UseQueryOptions) => {
  const { account: user } = useSession();

  const getAllPastFeedbacks = () => {
    const params = {
      profileId,
    };
    return matchingEngineApi.get(`past-feedbacks`, { params });
  };

  const queryKey = ['matching-engine', user?.organizationId, profileId, 'past-feedbacks'];

  const queryResponse = useQuery(queryKey, getAllPastFeedbacks, {
    enabled: !!profileId && options?.enabled,
    ...options,
  });

  return {
    ...queryResponse,
    data: queryResponse.data?.data || { pastFeedbacks: [] },
  };
};

const useGetTeamFeedbackInfo = (profileId: string) => {
  const { account: user } = useSession();

  const getReporteesFeedbacks = () => {
    const params = {
      profileId,
    };
    return matchingEngineApi.get(`team-feedback-info`, { params });
  };

  const queryKey = ['matching-engine', user?.organizationId, profileId, 'team-feedback-info'];

  const queryResponse = useQuery(queryKey, getReporteesFeedbacks, {
    enabled: !!profileId,
  });

  return {
    ...queryResponse,
    data: queryResponse.data?.data,
  };
};

const useGetSkillsDemonstratedForFeedback = (profileId: string, cutOffDays: number) => {
  const { account: user, hasOrganization } = useSession();

  const getSkillsDesmontrated = () => {
    return matchingEngineApi.post(`skills-demonstrated`, { profileId, cutOffDays });
  };

  const queryKey = [
    'matching-engine',
    user?.organizationId,
    profileId,
    'skills-demonstrated',
  ];

  const queryResponse = useQuery(queryKey, getSkillsDesmontrated, {
    enabled: !!(hasOrganization && profileId),
  });

  return {
    ...queryResponse,
    data: (queryResponse?.data?.data || {}) as {
      skillsDemonstrated: { id: string; name: string }[];
      workItems: WorkItem[];
    },
  };
};

const useAddFeedback = (options: UseMutationOptions = {}) => {
  const addFeedbackApi = (feedback: AddFeedbackParams) => {
    return matchingEngineApi.post('add-feedback', { ...feedback });
  };

  return useMutation(addFeedbackApi, {
    showSuccessSnackbar: true,
    ...options,
  });
};
